import React, { useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import FareRule from "./FareRule";
import { AuthContext } from "../../Routes/AuthProvider"
import * as XLSX from "xlsx";


/* ================= FARE UTILS ================= */
const calcFare = (fare = {}) => {
  return (
    Number(fare.totalBaseFare || 0) +
    Number(fare.totalFuelSurcharge || 0) +
    Number(fare.totalTax || 0)
  );
};

const IntRoundTrip = ({
  data,
  onwardFlights = [],
  returnFlights = [],
  loading,
  error,
  totalPax,
  requestBody,
  filteredOnwardFlights,
  filteredReturnFlights,
  handleBookNow,
}) => {
  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [sortType, setSortType] = useState("DEPARTURE");
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedFare, setSelectedFare] = useState(null);
  const [selectedFareIndex, setSelectedFareIndex] = useState(null);
  const [loadingFlightIndex, setLoadingFlightIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const { name, userName, roles, corporateId } = useContext(AuthContext);

  const [selectedFareMap, setSelectedFareMap] = useState({});
  const [fareRuleIndex, setFareRuleIndex] = useState(null);

  const [showAdSelctInt, setshowAdSelctInt] = useState(false);
  const [removedShareFaresInt, setRemovedShareFaresInt] = useState([]);


  const formatINR = (value) => {
    return Number(value || 0).toLocaleString("en-IN");
  };

  // copy

  console.log("selectedonwardFlights!!", onwardFlights);
  console.log("selectedreturnFlights", returnFlights);

  const [selectedShareFlightsInt, setSelectedShareFlightsInt] = useState([]);

  const handleShareFlightCheckbox = (flight, isChecked) => {
    const flightKey = flight.itemNo;

    setSelectedShareFlightsInt((prev) => {
      if (isChecked) {
        // Avoid selecting the same flight more than once.
        if (prev.some((item) => item.itemNo === flightKey)) {
          return prev;
        }

        return [...prev, flight];
      }

      // Remove only the unchecked flight.
      return prev.filter((item) => item.itemNo !== flightKey);
    });
  };


  const getShareAirport = (code) => code || "—";

  const getShareDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getShareAmount = (fare) => {
    const amount = fare?.totalFare ?? fare?.roundTripFare ?? 0;
    const number = Number(amount);

    return Number.isFinite(number) ? number : 0;
  };

  const getShareAmountText = (fare) =>
    getShareAmount(fare).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

  const getShareFareName = (fare, fareIndex) =>
    fare?.fareName ||
    fare?.fareType ||
    fare?.type ||
    `Fare ${fareIndex + 1}`;

  const getShareRefundStatus = (fare) => {
    const value = fare?.isRefundable;

    return value === true || value === 1 || value === "true"
      ? "Refundable"
      : "Non-refundable";
  };

  const escapeShareHtml = (value) =>
    String(value ?? "—")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getSelectedShareDataInt = () => {
    return selectedShareFlightsInt
      .map((flightIndex, optionIndex) => {
        const flight = returnFlights[flightIndex];

        if (!flight) return null;

        const segments = flight.segments || [];

        const onwardSegments = segments.filter(
          (segment) => Number(segment.flightCount) === 1
        );

        const returnSegments = segments.filter(
          (segment) => Number(segment.flightCount) === 2
        );

        const allSegments = [
          ...onwardSegments.map((segment) => ({
            ...segment,
            journeyLabel: "Onward",
          })),
          ...returnSegments.map((segment) => ({
            ...segment,
            journeyLabel: "Return",
          })),
        ];

        const fares = (flight.fares || [])
          .map((fare, fareIndex) => ({
            ...fare,
            fareIndex,
            fareKey: `${flightIndex}-${fareIndex}`,
          }))
          .filter(
            (fare) =>
              !removedShareFaresInt.includes(fare.fareKey)
          );

        const getRoute = (journeySegments) => {
          if (!journeySegments.length) return [];

          return [
            journeySegments[0].departAirportCode,
            ...journeySegments.map(
              (segment) => segment.arrivalAirportCode
            ),
          ].filter(Boolean);
        };

        return {
          flightIndex,
          optionIndex: optionIndex + 1,
          flight,
          allSegments,
          onwardSegments,
          returnSegments,
          onwardRoute: getRoute(onwardSegments),
          returnRoute: getRoute(returnSegments),
          fares,
        };
      })
      .filter(Boolean);
  };

  const handleDownloadSelectedFlightsInt = () => {
    const selectedFlights = getSelectedShareDataInt();

    if (!selectedFlights.length) {
      window.alert("Please select at least one flight option.");
      return;
    }

    const flightRows = [];
    const fareRows = [];

    selectedFlights.forEach((option) => {
      const {
        optionIndex,
        allSegments,
        onwardRoute,
        returnRoute,
        fares,
      } = option;

      allSegments.forEach((segment) => {
        const journeySegments =
          segment.journeyLabel === "Onward"
            ? option.onwardSegments
            : option.returnSegments;

        const route =
          segment.journeyLabel === "Onward"
            ? onwardRoute
            : returnRoute;

        flightRows.push({
          "Option": `Option ${optionIndex}`,
          "Journey": segment.journeyLabel,
          "Airline": segment.airLineName || "",
          "Airline Code": segment.airLineCode || "",
          "Flight Number": segment.flightNumber || "",
          "Departure Date & Time": getShareDateTime(
            segment.departDateTime
          ),
          "Departure Airport": getShareAirport(
            segment.departAirportCode
          ),
          "Arrival Date & Time": getShareDateTime(
            segment.arrivalDateTime
          ),
          "Arrival Airport": getShareAirport(
            segment.arrivalAirportCode
          ),
          "Duration": segment.duration || "",
          "Stops": journeySegments.length <= 1
            ? "Non-stop"
            : `${journeySegments.length - 1} stop(s)`,
          "Complete Route": route.join(" → "),
        });
      });

      fares.forEach((fare) => {
        fareRows.push({
          "Option": `Option ${optionIndex}`,
          "Onward Route": onwardRoute.join(" → "),
          "Return Route": returnRoute.join(" → "),
          "Fare Type": getShareFareName(fare, fare.fareIndex),
          "Amount (INR)": getShareAmount(fare),
          "Price Type": getShareRefundStatus(fare),
        });
      });
    });

    const workbook = XLSX.utils.book_new();

    const flightSheet = XLSX.utils.json_to_sheet(
      flightRows.length
        ? flightRows
        : [{ Message: "No flight segment details available" }]
    );

    flightSheet["!cols"] = [
      { wch: 14 },
      { wch: 12 },
      { wch: 24 },
      { wch: 14 },
      { wch: 16 },
      { wch: 24 },
      { wch: 18 },
      { wch: 24 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 42 },
    ];

    const fareSheet = XLSX.utils.json_to_sheet(
      fareRows.length
        ? fareRows
        : [{ Message: "No fares available" }]
    );

    fareSheet["!cols"] = [
      { wch: 14 },
      { wch: 32 },
      { wch: 32 },
      { wch: 24 },
      { wch: 18 },
      { wch: 20 },
    ];

    XLSX.utils.book_append_sheet(
      workbook,
      flightSheet,
      "Flight Details"
    );

    XLSX.utils.book_append_sheet(
      workbook,
      fareSheet,
      "Fares"
    );

    XLSX.writeFile(
      workbook,
      "Selected_International_Flights.xlsx"
    );
  };


  const handleCopySelectedFlightsInt = async () => {
    const selectedFlights = getSelectedShareDataInt();

    if (!selectedFlights || selectedFlights.length === 0) {
      window.alert("Please select at least one flight option.");
      return;
    }

    const tableStyle =
      "border-collapse:collapse;width:100%;font-family:Arial,Helvetica,sans-serif;font-size:12px;margin:10px 0 18px 0;";

    const thStyle =
      "border:1px solid #d5d5d5;background-color:#110942;color:#ffffff;padding:9px;text-align:center;font-weight:bold;";

    const tdStyle =
      "border:1px solid #d5d5d5;padding:8px;text-align:center;vertical-align:middle;";

    // Return an absolute URL so the image can load outside the React app.
    const getAirlineLogoUrl = (airLineCode) => {
      if (!airLineCode) return "";

      const code = String(airLineCode).trim();

      // Prevent invalid paths in the generated HTML.
      if (!/^[a-zA-Z0-9_-]+$/.test(code)) return "";

      return `${window.location.origin}/assets/images/Flight_Images/${encodeURIComponent(
        code
      )}.gif`;
    };

    let html = `
    <div style="font-family:Arial,Helvetica,sans-serif;color:#333333;background:#ffffff;">
      <p style="margin:0 0 12px 0;">Dear Sir/Ma'am,</p>
      <p style="margin:0 0 16px 0;">
        Please find the selected international round-trip flight options below.
      </p>
  `;

    let plainText =
      "Dear Sir/Ma'am,\n\n" +
      "Please find the selected international round-trip flight options below.\n\n";

    selectedFlights.forEach((option) => {
      const {
        optionIndex,
        allSegments = [],
        onwardRoute = [],
        returnRoute = [],
        onwardSegments = [],
        returnSegments = [],
        fares = [],
      } = option;

      html += `
      <table style="${tableStyle}">
        <thead>
          <tr>
            <th style="${thStyle}">Journey</th>
            <th style="${thStyle}">Flight</th>
            <th style="${thStyle}">Departure</th>
            <th style="${thStyle}">Arrived</th>
            <th style="${thStyle}">Duration</th>
            <th style="${thStyle}">Stops</th>
          </tr>
        </thead>
        <tbody>
    `;

      allSegments.forEach((segment) => {
        const journeySegments =
          segment.journeyLabel === "Onward"
            ? onwardSegments
            : returnSegments;

        const route =
          segment.journeyLabel === "Onward"
            ? onwardRoute
            : returnRoute;

        const airlineName = segment.airLineName || "";
        const airlineCode = segment.airLineCode || "";
        const flightNumber = segment.flightNumber || "";

        const logoUrl = getAirlineLogoUrl(airlineCode);

        const flightName =
          `${airlineName} ${airlineCode}` +
          (flightNumber ? `-${flightNumber}` : "");

        // Include the airline logo directly in the copied HTML.
        // The absolute URL allows the receiving email client to load it.
        const flightCellHTML = `
        <table
          cellpadding="0"
          cellspacing="0"
          border="0"
          style="border-collapse:collapse;margin:0 auto;"
        >
          <tr>
            ${logoUrl
            ? `
                  <td style="padding:4px 7px 4px 0;vertical-align:middle;">
                    <img
                      src="${escapeShareHtml(logoUrl)}"
                      alt="${escapeShareHtml(airlineName || airlineCode)}"
                      width="35"
                      style="display:block;width:35px;height:auto;border:0;outline:none;text-decoration:none;"
                    />
                  </td>
                `
            : ""
          }
            <td style="padding:4px;vertical-align:middle;text-align:left;">
              ${escapeShareHtml(flightName.trim())}
            </td>
          </tr>
        </table>
      `;

        const departureText =
          `${getShareDateTime(segment.departDateTime)} | ` +
          `${segment.departAirportCode || "—"}`;

        const arrivalText =
          `${getShareDateTime(segment.arrivalDateTime)} | ` +
          `${segment.arrivalAirportCode || "—"}`;

        const stopsText =
          journeySegments.length <= 1
            ? "Non-stop"
            : `${journeySegments.length - 1} stop(s)`;

        html += `
        <tr>
          <td style="${tdStyle}">
            ${escapeShareHtml(segment.journeyLabel)}
          </td>

          <td style="${tdStyle};text-align:left;">
            ${flightCellHTML}
          </td>

          <td style="${tdStyle}">
            ${escapeShareHtml(departureText)}
          </td>

          <td style="${tdStyle}">
            ${escapeShareHtml(arrivalText)}
          </td>

          <td style="${tdStyle}">
            ${escapeShareHtml(segment.duration || "—")}
          </td>

          <td style="${tdStyle}">
            ${escapeShareHtml(stopsText)}
          </td>
        </tr>
      `;

        plainText +=
          `${segment.journeyLabel}: ${flightName} | ` +
          `Departure: ${departureText} | ` +
          `Arrival: ${arrivalText} | ` +
          `Duration: ${segment.duration || "—"} | ` +
          `Stops: ${stopsText} \n`;
      });

      html += `
        </tbody>
      </table>

      <table style="${tableStyle}">
        <thead>
          <tr>
            <th style="${thStyle}">Fare Type</th>
            <th style="${thStyle}">Amount (INR)</th>
            <th style="${thStyle}">Price Type</th>
          </tr>
        </thead>
        <tbody>
    `;

      if (fares.length === 0) {
        html += `
        <tr>
          <td colspan="3" style="${tdStyle}">
            No fares available
          </td>
        </tr>
      `;

        plainText += "No fares available.\n";
      } else {
        fares.forEach((fare) => {
          const fareName = getShareFareName(fare, fare.fareIndex);
          const amount = getShareAmountText(fare);
          const refundStatus = getShareRefundStatus(fare);

          const refundColor =
            refundStatus === "Refundable" ? "green" : "red";

          html += `
          <tr>
            <td style="${tdStyle}">
              ${escapeShareHtml(fareName)}
            </td>

            <td style="${tdStyle}">
              ₹ ${escapeShareHtml(amount)}
            </td>

            <td style="${tdStyle};color:${refundColor};font-weight:500;">
              ${escapeShareHtml(refundStatus)}
            </td>
          </tr>
        `;

          plainText +=
            `Fare: ${fareName} | Amount: INR ${amount} | ` +
            `${refundStatus}\n`;
        });
      }

      html += `
        </tbody>
      </table>
        <div style="margin-top:10px;font-size:12px;color:#666;font-style:italic;">
    Note: The above-mentioned fares are subject to change at the time of ticket issuance, depending on airline availability.
</div>
    `;

      plainText += "\n";
    });

    html += "</div>";

    try {
      // Preferred method: copy formatted HTML and plain text together.
      if (
        navigator.clipboard?.write &&
        typeof ClipboardItem !== "undefined"
      ) {
        const htmlBlob = new Blob([html], {
          type: "text/html",
        });

        const textBlob = new Blob([plainText], {
          type: "text/plain",
        });

        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": htmlBlob,
            "text/plain": textBlob,
          }),
        ]);
      } else {
        // Fallback for browsers without rich clipboard support.
        const temporaryElement = document.createElement("div");

        temporaryElement.contentEditable = "true";
        temporaryElement.style.position = "fixed";
        temporaryElement.style.left = "-99999px";
        temporaryElement.style.top = "0";
        temporaryElement.style.width = "1000px";
        temporaryElement.innerHTML = html;

        document.body.appendChild(temporaryElement);

        const selection = window.getSelection();
        const range = document.createRange();

        range.selectNodeContents(temporaryElement);
        selection?.removeAllRanges();
        selection?.addRange(range);

        temporaryElement.focus();

        const copied = document.execCommand("copy");

        selection?.removeAllRanges();
        document.body.removeChild(temporaryElement);

        if (!copied) {
          throw new Error("Copy command was not successful.");
        }
      }

      window.alert(
        "Selected flight details copied. Open your email and paste them into the message."
      );
    } catch (error) {
      console.error("Copy selected flights failed:", error);

      window.alert(
        "Could not copy formatted flight details. Please allow clipboard access and try again."
      );
    }
  };



  /* ================= FORMATTERS ================= */
  const formatTime = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
      : "—";

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };
  const [loadFareRule, setLoadFareRule] = useState(false);
  const handleFinalBook = async (flight, index) => {
    setLoadingFlightIndex(index);
    await handleBookNow(flight, index);
    setLoadingFlightIndex(null);
  };

  /* ================= VIEW Excel Export ================= */



  /* ================= VIEW MORE FARE ================= */
  const handleViewMoreFare = (flight) => {
    if (!flight) return;
    //console.log("flight", flight);
    setSelectedFlight({
      ...flight
    });

    setSelectedFare(null);
    setSelectedFareIndex(null);

    setTimeout(() => {
      const el = document.getElementById("ViewMoreFare");
      if (el && window.bootstrap) {
        window.bootstrap.Offcanvas.getOrCreateInstance(el).show();
      }
    }, 50);
  };

  /* ================= RENDER ================= */


  // Show loader
  if (loading) {
    return (
      <p className="text-center load">
        <span className="loader"></span>
      </p>
    );
  }


  const fdDur = (d) => {
    if (!d) return "N/A";
    const [h, m] = String(d).split(":").map((n) => parseInt(n, 10) || 0);
    return `${h > 0 ? `${h}h ` : ""}${m > 0 ? `${m}m` : ""}`.trim() || "N/A";
  };

  const fdDiff = (from, to) => {
    if (!from || !to) return "";
    const diff = new Date(to) - new Date(from);
    if (!(diff > 0)) return "";
    const mins = Math.floor(diff / 60000);
    return fdDur(`${Math.floor(mins / 60)}:${mins % 60}`);
  };

  const fdDate = (dt) =>
    dt
      ? new Date(dt).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "N/A";

  const fdTime = (dt) => (dt ? formatTime(dt) : "N/A");

  const fdSameDay = (a, b) =>
    new Date(a).toDateString() === new Date(b).toDateString();

  const renderJourney = (segs = [], title, totalDur) => {
    if (!segs.length) return null;

    const first = segs[0] || {};
    const last = segs[segs.length - 1] || {};
    const stopCount = Math.max(segs.length - 1, 0);

    return (
      <div className="fd-wrap">
        {/* JOURNEY HEADER */}
        <div className="fd-hero fd-hero-sm">
          <div className="fd-journey-title">
            <i className="fa fa-plane"></i> {title}
          </div>

          <div className="fd-hero-route">
            <div className="fd-hero-city">
              <span className="fd-code">{first.departAirportCode}</span>
              <span className="fd-city">{first.departCityName}</span>
            </div>

            <div className="fd-hero-mid">
              <span className="fd-hero-dur">{fdDur(totalDur)}</span>
              <div className="fd-hero-line">
                <i className="fa fa-plane"></i>
              </div>
              <span className="fd-hero-stops">
                {stopCount === 0 ? "Non-stop" : `${stopCount} Stop${stopCount > 1 ? "s" : ""}`}
              </span>
            </div>

            <div className="fd-hero-city text-end">
              <span className="fd-code">{last.arrivalAirportCode}</span>
              <span className="fd-city">{last.arrivalCityName}</span>
            </div>
          </div>

          <div className="fd-chips">
            <span className="fd-chip">
              <i className="fa fa-calendar"></i> {fdDate(first.departDateTime)}
            </span>
          </div>
        </div>

        {/* SEGMENTS */}
        {segs.map((seg, index) => {
          const nextSeg = segs[index + 1];
          const layover =
            (seg.connectionTime && fdDur(seg.connectionTime)) ||
            (nextSeg && fdDiff(seg.arrivalDateTime, nextSeg.departDateTime));
          const overnight = !fdSameDay(seg.departDateTime, seg.arrivalDateTime);

          return (
            <React.Fragment key={`${title}-${seg.flightNumber || "f"}-${index}`}>
              <div className="fd-card">
                <div className="fd-airline">
                  <div className="fd-airline-left">
                    <img
                      className="fd-logo"
                      src={`/assets/images/Flight_Images/${seg.airLineCode}.gif`}
                      alt={seg.airLineName || "Airline"}
                    />
                    <div>
                      <strong>{seg.airLineName || "Airline"}</strong>
                      <small>
                        {seg.airLineCode}-{seg.flightNumber}
                        {seg.equipmentType ? ` • ${seg.equipmentType}` : ""}
                      </small>
                    </div>
                  </div>
                  {/* {stopCount > 0 && <span className="fd-badge">Flight {index + 1}</span>} */}
                </div>

                <div className="fd-timeline">
                  {/* Departure */}
                  <div className="fd-point">
                    <div className="fd-time">
                      <strong>{fdTime(seg.departDateTime)}</strong>
                      <small>{fdDate(seg.departDateTime)}</small>
                    </div>
                    <span className="fd-dot"></span>
                    <div className="fd-place">
                      <h4>
                        {seg.departCityName || seg.departAirportCode} ({seg.departAirportCode})
                      </h4>
                      <p>
                        {seg.departAirportName}
                        {seg.departTerminal && (
                          <span className="fd-term">Terminal {seg.departTerminal}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="fd-travel">
                    <i className="fa fa-clock"></i> {fdDur(seg.duration)}
                  </div>

                  {/* Arrival */}
                  <div className="fd-point">
                    <div className="fd-time">
                      <strong>{fdTime(seg.arrivalDateTime)}</strong>
                      <small>
                        {fdDate(seg.arrivalDateTime)}
                        {overnight && <em className="fd-next">+1 day</em>}
                      </small>
                    </div>
                    <span className="fd-dot fd-dot-end"></span>
                    <div className="fd-place">
                      <h4>
                        {seg.arrivalCityName || seg.arrivalAirportCode} ({seg.arrivalAirportCode})
                      </h4>
                      <p>
                        {seg.arrivalAirportName}
                        {seg.arrivalTerminal && (
                          <span className="fd-term">Terminal {seg.arrivalTerminal}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* LAYOVER */}
              {nextSeg && (
                <div className="fd-layover">
                  <i className="fa fa-hourglass-half"></i>
                  {seg.layoverMessage ? (
                    <>
                      <span>
                        <strong>{seg.layoverMessage}</strong>
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        Layover at{" "}
                        <strong>
                          {seg.arrivalCityName || seg.arrivalAirportName} ({seg.arrivalAirportCode})
                        </strong>
                        {" • "}
                        <strong>{layover || "N/A"}</strong>
                        {seg.changeOfPlane ? " • Change of plane" : ""}
                      </span>
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const onwardSegs = selectedFlight?.onwardSegments?.length
    ? selectedFlight.onwardSegments
    : selectedFlight?.segments || [];
  const returnSegs = selectedFlight?.returnSegments || [];

  // No flights
  // if (!loading && returnFlights.length === 0) {
  //   return (
  //     <p className="text-center">
  //       No International Round-Trip Flights Found
  //     </p>
  //   );
  // }

  return (
    <>
      {/* ================= SORT BAR ================= */}
      <div className="row ruslt_sort mb-2 sticky">

        <div className="col-md-10 col-lg-10">
          <h3 className="destnation">
            {/* Onward Route */}
            {returnFlights?.[0]?.segments?.find(
              (seg) => seg.flightCount === 1
            )?.departAirportCode || data.departureQuery?.split("(")[1]?.split(")")[0]}

            <i className="fa fa-arrow-right"></i>{" "}

            {(() => {
              const onwardSegments =
                returnFlights?.[0]?.segments?.filter(
                  (seg) => seg.flightCount === 1
                ) || [];

              return (
                onwardSegments[onwardSegments.length - 1]?.arrivalAirportCode ||
                data.arrivalQuery?.split("(")[1]?.split(")")[0]
              );
            })()}
            {/* Onward Date */}
            <span style={{ color: "red" }}>
              {" || "}
              {formatDateForDisplay(data.onwardDate)}
            </span>

            {" || "}

            {/* Return Route */}
            {(() => {
              const returnSegments =
                returnFlights?.[0]?.segments?.filter(
                  (seg) => seg.flightCount === 2
                ) || [];

              if (returnSegments.length === 0) return null;

              return (
                <>

                  {returnSegments[0]?.departAirportCode}
                  <i className="fa fa-arrow-right" style={{ margin: "0 5px" }}></i>
                  {returnSegments[returnSegments.length - 1]?.arrivalAirportCode}

                </>
              );
            })()}
            {/* Return Date */}
            {(() => {
              const returnDate =
                returnFlights?.[0]?.segments?.find(
                  (seg) => seg.flightCount === 2
                )?.departDateTime;

              return returnDate ? (
                <span style={{ color: "red" }}>
                  {" || "}
                  {formatDateForDisplay(returnDate)}
                </span>
              ) : null;
            })()}
            {" || "}
            {/* Passenger Count */}
            {[
              Number(data.adult) > 0 &&
              `${data.adult} Adult${Number(data.adult) > 1 ? "s" : ""}`,
              Number(data.child) > 0 &&
              `${data.child} Child${Number(data.child) > 1 ? "ren" : ""}`,
              Number(data.infant) > 0 &&
              `${data.infant} Infant${Number(data.infant) > 1 ? "s" : ""}`,
            ]
              .filter(Boolean)
              .join(", ")}




            {/* Preferred Class */}
            {" || "}
            {requestBody?.segments?.[0]?.preferredClass}

            {/* Fare Type */}
            {data?.fareType && (
              <span style={{ color: "#ff0000", marginLeft: "8px" }}>
                {" || "}
                {data.fareType}
              </span>
            )}

            {/* Total Flight Options */}
            {returnFlights?.length > 0 && (
              <span style={{ color: "#ff0000", marginLeft: "10px" }}>
                {" || "}Flights: {returnFlights.length}
              </span>
            )}
          </h3>
        </div>

        <div className="col-md-2 col-lg-2 text-center">
          <button
            className="modify_btn_sr"
            data-bs-toggle="modal"
            data-bs-target="#ModifyResult"
          >
            Modify Result <i className="fa fa-arrow-right"></i>
          </button>

        </div>
      </div>


      <div className="result_repeat mb-2">
        <div className="d-flex-reco intRound">
          <div
            className={`fastest ${sortType === "CHEAPEST" ? "active" : ""}`}
            onClick={() => setSortType("CHEAPEST")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSortType("CHEAPEST")}
          >
            <img src="/assets/images/rupee.png" alt="Cheapest" />
            <h4>Cheapest</h4>
          </div>

          <div
            className={`fastest ${sortType === "FASTEST" ? "active" : ""}`}
            onClick={() => setSortType("FASTEST")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSortType("FASTEST")}
          >
            <img src="/assets/images/speed.png" alt="Fastest" />
            <h4>Fastest</h4>
          </div>

          <div
            className={`fastest ${sortType === "NONSTOP" ? "active" : ""}`}
            onClick={() => setSortType("NONSTOP")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSortType("NONSTOP")}
          >
            <img src="/assets/images/flight-blue.png" alt="Non Stop" />
            <h4>Non Stop</h4>
          </div>

          <div
            className={`fastest ${sortType === "DEPARTURE" ? "active" : ""}`}
            onClick={() => setSortType("DEPARTURE")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSortType("DEPARTURE")}
          >
            <img src="/assets/images/flight-blue.png" alt="Departure" />
            <h4>Departure</h4>
          </div>
          <div
            className="ExportToExcl"
            // onClick={() => ExportToExcel()}
            role="button"
            tabIndex={0}
          >
            <img src="/assets/images/excel.png" alt="Export To Excel" />
            <h4>Export To Excel</h4>
          </div>
          <div
            className="share_it"
            onClick={() => setshowAdSelctInt((prev) => !prev)}
            role="button"
            tabIndex={0}
          >
            <img src="/assets/images/share.png" alt="share flights" />
            <h4>Share IT</h4>
          </div>

          {showAdSelctInt && (
            <div className="shareit" data-bs-toggle="modal" data-bs-target="#shareFlightsInt"> :
              <img src="/assets/images/copy.png" alt="share flights Details" />
            </div>
          )}
        </div>

      </div>

      {/* ================= LIST ================= */}
      {/* {returnFlights?.length > 0 ? ( */}
      {returnFlights?.length > 0 ? (returnFlights.map((item, idx) => {
        const onwardSegments = item.segments?.filter(x => x.flightCount === 1) || [];
        const returnSegments = item.segments?.filter(x => x.flightCount === 2) || [];

        const onwardFirst = onwardSegments[0];
        const onwardLast = onwardSegments[onwardSegments.length - 1];

        const returnFirst = returnSegments[0];
        const returnLast = returnSegments[returnSegments.length - 1];

        return (
          <div className="result_repeat mb-3" key={idx}>
            <div className="row">
              {/* ---------- LEFT SIDE (FLIGHT DETAILS) ---------- */}
              <div className="col-md-9">
                {/* ----- ONWARD ----- */}
                <div className="row oneWay_flight_1">
                  <p className="mode_trvel">
                    <span>
                      <i className="fa fa-plane"></i> Departure
                    </span>
                  </p>

                  <div className="col-md-4 col-lg-4">
                    <div className="row">
                      <div className="col-md-4">
                        <img
                          src={`/assets/images/Flight_Images/${onwardFirst?.airLineCode}.gif`}
                          width="30"
                          alt=""
                        />
                      </div>
                      <div className="col-md-8">
                        <span>
                          {onwardSegments?.map((seg, i) => (
                            <span key={i}>
                              {seg.airLineCode}-{seg.flightNumber}
                              {i < onwardSegments.length - 1 && "/"}
                            </span>
                          ))}
                        </span>
                        <div>{onwardFirst.airLineName}</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-8 border-left-right">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="timer">
                          <h4>{onwardFirst.departDateTime?.slice(11, 16)}</h4>
                          <p>{onwardFirst.departAirportCode}</p>
                        </div>
                      </div>


                      <div className="col-md-3">
                        <div className="deruation">
                          <p>{item.totalDuration}</p>

                          <div className="bordr_top">
                            <i className="fa fa-plane"></i>
                          </div>
                          <p className="stops">
                            {onwardSegments.length - 1}{" "}
                            {(onwardSegments.length - 1) === 1 ? "stop" : "stops"}
                          </p>
                        </div>
                      </div>

                      <div className="col-md-4 offset-1">
                        <div className="timer">
                          <h4>{onwardLast.arrivalDateTime?.slice(11, 16)}</h4>
                          <p>{onwardLast.arrivalAirportCode}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ----- RETURN ----- */}
                <div className="row oneWay_flight_2">
                  <p className="mode_trvel">
                    <span>
                      <i className="fa fa-plane"></i> Return
                    </span>
                  </p>

                  <div className="col-md-4">
                    <div className="row">
                      <div className="col-md-4">
                        <img
                          src={`/assets/images/Flight_Images/${returnFirst?.airLineCode}.gif`}
                          width="30"
                          alt=""
                        />
                      </div>
                      <div className="col-md-8">
                        <span>
                          {returnSegments?.map((seg, i) => (
                            <span key={i}>
                              {seg.airLineCode}-{seg.flightNumber}
                              {i < returnSegments.length - 1 && "/"}
                            </span>
                          ))}
                        </span>
                        <div>{returnFirst.airLineName}</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-8 border-left-right">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="timer">
                          <h4>{returnFirst?.departDateTime?.slice(11, 16)}</h4>
                          <p>{returnFirst?.departAirportCode}</p>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="deruation">
                          <p>{item.totalDurationReturn}</p>
                          <div className="bordr_top">
                            <i className="fa fa-plane"></i>
                          </div>
                          <p className="stops">
                            {returnSegments.length - 1}{" "}
                            {(returnSegments.length - 1) === 1 ? "stop" : "stops"}
                          </p>
                        </div>
                      </div>

                      <div className="col-md-4 offset-1">
                        <div className="timer">
                          <h4>{returnLast.arrivalDateTime?.slice(11, 16)}</h4>
                          <p>{returnLast.arrivalAirportCode}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ----- Services / Refundability ----- */}
                <div className="col-md-12">
                  <ul className="list_service">

                    {/* Equipment + Seats */}
                    <li>
                      <div className="d-flex">
                        <div className="equipmentType">
                          <i className="fa-solid fa-fan"></i>
                          <span>{onwardFirst?.equipmentType || "-"}</span>
                        </div>

                        <div className="equipmentType">
                          <img
                            className="seats"
                            src="/assets/images/seat.png"
                            alt="seat"
                          />
                          <span>{onwardFirst?.availableSeats ?? "-"} Seats</span>
                        </div>
                      </div>
                    </li>

                    {/* Refundable */}
                    <li className="refundable">
                      {item?.fares?.[0]?.isRefundable ? (
                        <span style={{ color: "green", fontWeight: 500 }}>Refundable</span>
                      ) : (
                        <span style={{ color: "red", fontWeight: 500 }}>Non-Refundable</span>
                      )}
                    </li>
                    {["TMC Operations", "TMC Manager"].some(role => roles?.includes(role)) && (
                      <li className="view_on">
                        <p class="policyStatus text-success">{item?.supSource}</p>
                      </li>
                    )}

                    {/* Flight Details */}
                    <li className="view_on">
                      <a
                        className="flight_details"
                        role="button"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#FlightDetails"
                        onClick={() =>
                          setSelectedFlight({
                            ...item,
                            onwardSegments,
                            returnSegments,
                            selectedFareIndex: 0
                          })
                          //</li>//console.log("setSelectedFlight", returnSegments)
                        }
                      >
                        <i className="fa fa-plane"></i> Flight Details
                      </a>
                    </li>


                  </ul>
                </div>

              </div>

              {/* ---------- RIGHT SIDE (PRICE + BUTTONS) ---------- */}

              <div className="col-md-3">
                <div className="price_int">
                  {showAdSelctInt && (
                    <div className="form-check w-100 d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedShareFlightsInt.includes(idx)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedShareFlightsInt((prev) => [
                              ...prev,
                              idx,
                            ]);
                          } else {
                            setSelectedShareFlightsInt((prev) =>
                              prev.filter((selectedIndex) => selectedIndex !== idx)
                            );
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className="cos_int_flight">
                    <i className="fa fa-inr"></i>{" "}
                    {formatINR(item?.fares?.[0]?.totalFare)}
                  </div>
                  <p className="type_frs">
                    {
                      item?.fares?.[0]?.fareType ??
                      "Standard Fare"
                    }
                  </p>
                  <p
                    className={`policyStatus ${item?.fares?.[0]?.policyStatus === "WithinPolicy"
                      ? "text-success"
                      : item?.fares?.[0]?.policyStatus === "OutOfPolicy"
                        ? "text-danger"
                        : ""
                      }`}
                  >
                    {item?.fares?.[0]?.policyStatus && (
                      <img
                        src={
                          item?.fares?.[0]?.policyStatus === "WithinPolicy"
                            ? "/assets/images/approved.png"
                            : item?.fares?.[0]?.policyStatus === "OutOfPolicy"
                              ? "/assets/images/cancelled.png"
                              : ""
                        }
                        alt="status"
                        style={{ marginRight: "6px" }}
                      />
                    )}

                    {item?.fares?.[0]?.policyStatus}
                  </p>



                  <button
                    className="view_more"
                    onClick={() => handleViewMoreFare(item)}
                  >
                    View More Fare
                  </button>

                  <button
                    className="book_now_fly"
                    onClick={() => {
                      const selectedFare = item.fares?.[0];
                      const updatedFlight = {
                        ...item,
                        selectedFare,
                        selectedFareIndex: 0
                      };
                      //console.log("BOOK:", selectedFare?.fareUniqueKey);
                      handleBookNow(updatedFlight, 0);
                    }}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })
      ) : (
        <div className="text-center py-4">No International Round-Trip Flights Found</div>
      )
      }


      {/* ================= OFFCANVAS More Fare ================= */}
      <div
        className="offcanvas offcanvas-end"
        id="ViewMoreFare"
        tabIndex="-1"
      >
        <div className="offcanvas-header">
          <h5>More Fare Options</h5>
          <button className="btn-close" data-bs-dismiss="offcanvas" />
        </div>

        <div className="offcanvas-body">
          {selectedFlight ? (
            <>
              <div className="vie_fare d-flex align-items-center gap-3">
                <div className="from_to">
                  <img
                    alt={selectedFlight.segments?.[0]?.airLineName || "airline"}
                    src={`/assets/images/Flight_Images/${selectedFlight.segments?.[0]?.airLineCode}.gif`}
                  />
                </div>
                <div className="flight_time">
                  <span>
                    <strong>
                      {selectedFlight.segments?.[0]?.departCityName} ({selectedFlight.segments?.[0]?.departAirportCode}) →{" "}
                      {
                        (() => {
                          const onwardSegments = selectedFlight?.segments?.filter(x => x.flightCount === 1) || [];
                          const lastSeg = onwardSegments[onwardSegments.length - 1];

                          return lastSeg
                            ? `${lastSeg.arrivalCityName} (${lastSeg.arrivalAirportCode})`
                            : "";
                        })()
                      }
                    </strong>
                  </span>
                  <span>
                    <strong>{selectedFlight.segments?.[0]?.airLineName}</strong>
                  </span>
                  <span>
                    <strong>
                      {new Date(
                        selectedFlight.segments?.[0]?.departDateTime
                      ).toDateString()}
                    </strong>
                  </span>
                  <span>
                    Departure at:{" "}
                    <strong>{formatTime(selectedFlight.segments?.[0]?.departDateTime)}</strong>
                  </span>
                  <span>
                    Arrival at:{" "}
                    <strong>{(() => {
                      const onwardSegments = selectedFlight?.segments?.filter(x => x.flightCount === 1) || [];
                      const lastSeg = onwardSegments.at(-1);
                      return lastSeg ? formatTime(lastSeg.arrivalDateTime) : "";
                    })()}</strong>
                  </span>
                </div>
              </div>

              <div className="d-flex flex-wrap mt-3">
                {selectedFlight.fares?.length > 0 ? (
                  selectedFlight.fares.map((fare, index) => (
                    <div key={index} className="Select_new_fare repeat_section">
                      <h5>{fare.fareType || "Standard Fare"}</h5>
                      <p
                        className={`policyStatus text-en ${fare?.policyStatus === "WithinPolicy"
                          ? "text-success"
                          : fare?.policyStatus === "OutOfPolicy"
                            ? "text-danger"
                            : ""
                          }`}
                      >
                        {fare?.policyStatus && (
                          <img
                            src={
                              fare.policyStatus === "WithinPolicy"
                                ? "/assets/images/approved.png"
                                : fare.policyStatus === "OutOfPolicy"
                                  ? "/assets/images/cancelled.png"
                                  : ""
                            }
                            alt="status"
                            style={{ marginRight: "6px" }}
                          />
                        )}

                        {fare.policyStatus}
                      </p>
                      <h3>
                        <i className="fa fa-inr"></i>{" "}
                        {formatINR(fare.totalFare)}
                      </h3>

                      <div className="riepet d-flex flex-wrap justify-content-between">

                        <div className="baggage">
                          <h4>
                            <i className="fa fa-suitcase-rolling"></i> Fare Inclusions
                          </h4>

                          <ul>
                            {fare.fareInclusions && fare.fareInclusions.length > 0 ? (
                              fare.fareInclusions.map((item, i) => (
                                <li key={i}> {item}</li>
                              ))
                            ) : (
                              <li>No fare inclusions available</li>
                            )}
                          </ul>
                        </div>



                      </div>

                      <div className="d-flex-btn d-flex justify-content-between align-items-center mt-2">
                        <p
                          className="refun"
                          style={{
                            color: fare.isRefundable ? "green" : "red",
                            fontWeight: "500",
                          }}
                        >
                          {fare.isRefundable ? "Refundable" : "Non Refundable"}
                        </p>
                        <p
                          className="faReRule"
                          data-bs-toggle="modal"
                          data-bs-target="#FareRuleModal"
                          onClick={() => {
                            if (!selectedFlight?.itemNo) return;
                            setLoadFareRule(true);
                            setFareRuleIndex(index);
                          }}
                        >
                          Fare Rule
                        </p>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setSelectedFare({
                              ...fare,
                              totalFare: fare.roundTripFare,
                            });

                            setSelectedFareIndex(index);

                            // ✅ ADD THIS (IMPORTANT)
                            setSelectedFareMap(prev => ({
                              ...prev,
                              [selectedFlight.itemNo]: {
                                ...fare,
                                selectedIndex: index
                              }
                            }));
                            setSelectedFareIndex(index);
                          }}
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No fare options available</p>
                )}
              </div>
            </>
          ) : (
            <p>No flight selected</p>
          )}

        </div>
        {selectedFare && (
          <div className="total_amount border-top p-3">
            <p className="Grand">Grand Total <span style={{ color: 'red' }}>({selectedFare.fareType || ""})</span> </p>
            <h1>
              <i className="fa fa-inr"></i>{" "}
              {formatINR(
                selectedFlight?.fares?.[selectedFareIndex]?.totalFare ?? 0
              )}
            </h1>

            <button
              className="btn btn-primary"
              onClick={() => {

                const selectedFareData =
                  selectedFlight?.fares?.[selectedFareIndex ?? 0];

                const updatedFlight = {
                  ...selectedFlight,
                  selectedFare: selectedFareData,
                  selectedFareIndex: selectedFareIndex ?? 0
                };



                handleBookNow(updatedFlight, selectedFareIndex);
              }}
              disabled={loadingFlightIndex === selectedFareIndex}
              data-bs-dismiss="offcanvas"
            >
              Book Now
            </button>

          </div>
        )}

      </div>

      {/* ===================== Offcanvas for Flight Details ================ */}


      <div className="offcanvas offcanvas-end" tabIndex="-1" id="FlightDetails" aria-labelledby="FlightDetailsLabel">
        <div className="offcanvas-header">
          <h1 className="offcanvas-title">Flight Details</h1>

          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => {
              setActiveTab("info");
              setLoadFareRule(false);
            }}
          ></button>
        </div>

        <div className="offcanvas-body">
          {selectedFlight ? (
            <>
              {/* Tabs */}

              <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
                {/* Flight Info */}
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "info" ? "active" : ""}`}
                    data-bs-toggle="tab"
                    href="#kt_tab_pane_4"
                    onClick={() => setActiveTab("info")}
                  >
                    Flight Information
                  </a>
                </li>

                {/* Fare Rules */}
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "fare" ? "active" : ""}`}
                    data-bs-toggle="tab"
                    href="#kt_tab_pane_5"
                    onClick={() => {
                      setActiveTab("fare");

                      if (!selectedFlight?.itemNo) return;
                      setLoadFareRule(true);
                    }}
                  >
                    Fare Rules & Cancellation Change Rule
                  </a>
                </li>

                {/* Baggage */}
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "baggage" ? "active" : ""}`}
                    data-bs-toggle="tab"
                    href="#kt_tab_pane_6"
                    onClick={() => setActiveTab("baggage")}
                  >
                    Baggage Information
                  </a>
                </li>



              </ul>

              <div className="tab-content" id="myTabContent">
                {/* <div className="tab-pane fade show active" id="kt_tab_pane_4" role="tabpanel">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="int_don_flight">
                        {selectedFlight.onwardSegments?.length > 0 && (
                          <div> 
                            <p className="mode_trvel">
                              <span><i className="fa fa-plane"></i> Departure </span>
                            </p>

                            <div className="d-flex-v">
                              <div className="d-flex-v-f">
                                <h4>{formatTime(selectedFlight.segments[0]?.departDateTime)}</h4>
                              </div>
                              <div className="d-flex-v-m"><span className="zero"></span></div>
                              <div className="d-flex-v-l">
                                <h4>{selectedFlight.origin}</h4>
                                <p>
                                  {selectedFlight.segments[0]?.departAirportName}
                                  {selectedFlight.segments[0]?.departTerminal
                                    ? `,  ${selectedFlight.segments[0]?.departTerminal}`
                                    : ""}
                                </p>
                              </div>
                            </div>

                            <div className="d-flex-v time_to_frm">
                              <div className="d-flex-v-l m-autto">
                                {selectedFlight.onwardSegments.map((seg, index, arr) => {
                                  const departDate = new Date(seg.departDateTime);
                                  const arrivalDate = new Date(seg.arrivalDateTime);

                                  const getDurationBetween = (fromTime, toTime) => {
                                    const diffMs = new Date(toTime) - new Date(fromTime);
                                    if (diffMs <= 0) return "";
                                    const minutes = Math.floor(diffMs / 60000);
                                    const hrs = Math.floor(minutes / 60);
                                    const mins = minutes % 60;
                                    return `${hrs > 0 ? hrs + "h " : ""}${mins}m`;
                                  };

                                  const departTime = departDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                                  const arrivalTime = arrivalDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                                  const isLast = index === arr.length - 1;
                                  const nextSeg = arr[index + 1];
                                  const layoverDuration = nextSeg && getDurationBetween(seg.arrivalDateTime, nextSeg.departDateTime);

                                  return (
                                    <div key={index}>
                                      <div className="d-flex mb-3 align-items-center">
                                        <div className="d-flex-v-f m-autto me-3">
                                          <h6>{departDate.toDateString()}</h6>
                                          <p className="mb-0">{departTime} ➜ {arrivalTime}</p>
                                        </div>

                                        <div className="d-flex-v-m fhty_rg">
                                          <i className="fa fa-plane flights"></i>
                                        </div>

                                        <div className="d-flex">
                                          <div className="img_flt">
                                            <img
                                              className="flight_i"
                                              src={`/assets/images/Flight_Images/${seg.airLineCode}.gif`}
                                              alt={seg.airLineName}
                                            />
                                          </div>
                                          <div className="flt_name ms-2">
                                            {seg.airLineName} – {seg.airLineCode}-{seg.flightNumber}
                                          </div>
                                        </div>
                                      </div>

                                      {!isLast && layoverDuration && (
                                        <div className="layover-info">
                                          <p className="mb-0">
                                            <strong>Stop:</strong> {seg.arrivalCity || seg.arrivalAirportName || seg.destination}
                                          </p>
                                          <h4 className="text-red">Layover: {layoverDuration}</h4>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="d-flex-v">
                              {(() => {
                                const segments = selectedFlight.onwardSegments || [];
                                const lastSeg = segments[segments.length - 1] || {};

                                return (
                                  <>
                                    <div className="d-flex-v-f">
                                      <h4>{formatTime(lastSeg.arrivalDateTime)}</h4>
                                    </div>

                                    <div className="d-flex-v-m">
                                      <span className="zero"></span>
                                    </div>

                                    <div className="d-flex-v-l">
                                      <h4>{selectedFlight.destination || lastSeg.destination}</h4>
                                      <p>
                                        {lastSeg.arrivalAirportName}
                                        {lastSeg.arrivalTerminal ? `,  ${lastSeg.arrivalTerminal}` : ""}
                                      </p>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                    <div className="col-md-6">

                      {selectedFlight.returnSegments?.length > 0 && (
                        <div className="int_don_flight">
                          <p className="mode_trvel">
                            <span><i className="fa fa-plane"></i> Return </span>
                          </p>

                          <div className="d-flex-v">
                            <div className="d-flex-v-f">
                              <h4>{formatTime(selectedFlight.returnSegments[0]?.departDateTime)}</h4>
                            </div>
                            <div className="d-flex-v-m"><span className="zero"></span></div>
                            <div className="d-flex-v-l">
                              <h4>{selectedFlight.destination}</h4>
                              <p>
                                {selectedFlight.returnSegments[0]?.departAirportName}
                                {selectedFlight.returnSegments[0]?.departTerminal
                                  ? `,  ${selectedFlight.returnSegments[0]?.departTerminal}`
                                  : ""}
                              </p>
                            </div>
                          </div>

                          <div className="d-flex-v time_to_frm">
                            <div className="d-flex-v-l m-autto">
                              {selectedFlight.returnSegments.map((seg, index, arr) => {
                                const departDate = new Date(seg.departDateTime);
                                const arrivalDate = new Date(seg.arrivalDateTime);

                                const getDurationBetween = (fromTime, toTime) => {
                                  const diffMs = new Date(toTime) - new Date(fromTime);
                                  if (diffMs <= 0) return "";
                                  const minutes = Math.floor(diffMs / 60000);
                                  const hrs = Math.floor(minutes / 60);
                                  const mins = minutes % 60;
                                  return `${hrs > 0 ? hrs + "h " : ""}${mins}m`;
                                };

                                const departTime = departDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                                const arrivalTime = arrivalDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                                const isLast = index === arr.length - 1;
                                const nextSeg = arr[index + 1];
                                const layoverDuration = nextSeg && getDurationBetween(seg.arrivalDateTime, nextSeg.departDateTime);

                                return (
                                  <div key={index}>
                                    <div className="d-flex mb-3 align-items-center">
                                      <div className="d-flex-v-f m-autto me-3">
                                        <h6>{departDate.toDateString()}</h6>
                                        <p className="mb-0">{departTime} ➜ {arrivalTime}</p>
                                      </div>

                                      <div className="d-flex-v-m fhty_rg">
                                        <i className="fa fa-plane flights"></i>
                                      </div>

                                      <div className="d-flex">
                                        <div className="img_flt">
                                          <img
                                            className="flight_i"
                                            src={`/assets/images/Flight_Images/${seg.airLineCode}.gif`}
                                            alt={seg.airLineName}
                                          />
                                        </div>
                                        <div className="flt_name ms-2">
                                          {seg.airLineName} – {seg.airLineCode}-{seg.flightNumber}
                                        </div>
                                      </div>
                                    </div>

                                    {!isLast && layoverDuration && (
                                      <div className="layover-info">
                                        <p className="mb-0">
                                          <strong>Stop:</strong> {seg.arrivalCity || seg.arrivalAirportName || seg.destination}
                                        </p>
                                        <h4 className="text-red">Layover: {layoverDuration}</h4>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="d-flex-v">
                            {(() => {
                              const segments = selectedFlight.returnSegments || [];
                              const lastSeg = segments[segments.length - 1] || {};

                              return (
                                <>
                                  <div className="d-flex-v-f">
                                    <h4>{formatTime(lastSeg.arrivalDateTime)}</h4>
                                  </div>

                                  <div className="d-flex-v-m">
                                    <span className="zero"></span>
                                  </div>

                                  <div className="d-flex-v-l">
                                    <h4>{selectedFlight.origin || lastSeg.destination}</h4>
                                    <p>
                                      {lastSeg.arrivalAirportName}
                                      {lastSeg.arrivalTerminal ? `,  ${lastSeg.arrivalTerminal}` : ""}
                                    </p>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div> */}
                <div
                  className={`tab-pane fade ${activeTab === "info" ? "show active" : ""}`}
                  id="kt_tab_pane_4"
                  role="tabpanel"
                >
                  <div className="row">
                    <div className={returnSegs.length > 0 ? "col-md-6" : "col-md-12"}>
                      {renderJourney(onwardSegs, "Onward", selectedFlight?.totalDuration)}
                    </div>

                    {returnSegs.length > 0 && (
                      <div className="col-md-6">
                        {renderJourney(returnSegs, "Return", selectedFlight?.totalDurationReturn)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="tab-pane fade" id="kt_tab_pane_5" role="tabpanel">
                  <div className="row">
                    <div className="col-md-12">
                      <FareRule
                        load={loadFareRule}
                        clientCode={requestBody?.clientCode}
                        keyValue={requestBody?.key}
                        supplierCode={requestBody?.supplierCode}
                        departAirportCode={requestBody?.origin}
                        arrivalAirportCode={requestBody?.destination}
                        resultIndex={selectedFlight?.itemNo}
                        sessionID={requestBody?.sessionID}
                        flight={selectedFlight}
                        index={0}
                      />
                    </div>

                  </div>
                </div>
                <div className="tab-pane fade" id="kt_tab_pane_6" role="tabpanel">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Airline</th>
                        <th>Fare Type</th>
                        <th>Check-in Baggage</th>
                        <th>Cabin Baggage</th>
                      </tr>
                      {/* {(selectedFlight.fares || []).map((fare, fareIndex) => ( */}
                        <tr>
                          <td>
                            <img
                              className="flight_name"
                              src={`/assets/images/Flight_Images/${selectedFlight.segments?.[0]?.airLineCode}.gif`}
                              alt={selectedFlight.segments?.[0]?.airLineName}
                            />
                            <p className="plane_name">
                              {selectedFlight.segments?.[0]?.airLineName}
                            </p>
                             <p className="code_plne">{selectedFlight.segments?.[0]?.airLineCode}-
                            {selectedFlight.segments?.[0]?.flightNumber}</p>
                          </td>

                          <td>{selectedFlight.fares?.[0]?.journeyFareInfos?.[0]?.type}</td>
                          <td>
                            {selectedFlight.fares?.[0]?.journeyFareInfos?.[0]?.baggageDetails?.[0]?.baggage}
                          </td>
                          <td>
                            {selectedFlight.fares?.[0]?.journeyFareInfos?.[0]?.baggageDetails?.[0]?.cabinBaggage}
                          </td>
                        </tr>
                      {/* ))} */}
                      {/* <tr>
                        <td>
                          <img className="flight_name"
                            src={`/assets/images/Flight_Images/${selectedFlight.segments?.[0]?.airLineCode}.gif`} alt={selectedFlight.segments?.[0]?.airLineName} />
                          <p className="plane_name">{selectedFlight.segments?.[0]?.airLineName}</p>
                          <p className="code_plne">{selectedFlight.segments?.[0]?.airLineCode}-
                            {selectedFlight.segments?.[0]?.flightNumber}</p>

                        </td>
                        <td>15kgs</td>
                        <td>7kgs</td>
                      </tr>
                      <tr>
                        <td>
                          <img className="flight_name" src={`/assets/images/Flight_Images/${selectedFlight.segments?.[0]?.airLineCode}.gif`} alt={selectedFlight.segments?.[0]?.airLineName} />
                          <p className="plane_name">{selectedFlight.segments?.[0]?.airLineName}</p>
                          <p className="code_plne">{selectedFlight.segments?.[0]?.airLineCode}-
                            {selectedFlight.segments?.[0]?.flightNumber}</p>
                        </td>
                        <td>15kgs</td>
                        <td>7kgs</td>
                      </tr>*/}
                      <tr> 
                        <td colspan="4">
                          <div className="termsbagg">
                            <ul>
                              <li>Baggage information mentioned above is obtained from airline's reservation system, Berry Journeys does not guarantee the accuracy of this information.</li>
                              <li>The baggage allowance may vary according to stop-overs, connecting flights. changes in airline rules. etc.</li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ================= TOTAL ================= */}
              <div className="total_amount">
                <p className="Grand">Grand Total</p>
                <h1>
                  <i className="fa fa-inr"></i>{" "}
                  {Number(selectedFlight?.fares?.[0]?.totalFare ?? 0).toLocaleString("en-IN")}
                </h1>

                <button
                  className="btn btn-primary"
                  onClick={() => handleFinalBook(selectedFlight, selectedFareIndex ?? 0)}
                  disabled={loadingFlightIndex && loadingFlightIndex === selectedFareIndex}
                >
                  {loadingFlightIndex && loadingFlightIndex === selectedFareIndex
                    ? "Checking Price..."
                    : "Book Now"}
                </button>

              </div>

            </>
          ) : (
            <p>No flight selected</p>
          )}
        </div>

      </div>

      {/* Fare Rule For Perticular Fare */}

      <div className="modal fade" id="FareRuleModal" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">

            <div className="modal-header">
              <h4 className="modal-title">Fare Rule</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                onClick={() => setLoadFareRule(false)}
              ></button>
            </div>

            <div className="modal-body">
              <div className="row">
                {selectedFlight && loadFareRule && fareRuleIndex !== null && (
                  <>
                    <div className="col-md-12">
                      <FareRule
                        key={`${selectedFlight?.itemNo}_${loadFareRule}_${Date.now()}`}
                        load={loadFareRule}
                        clientCode={requestBody?.clientCode}
                        keyValue={requestBody?.key}
                        supplierCode={requestBody?.supplierCode}
                        departAirportCode={requestBody?.origin}
                        arrivalAirportCode={requestBody?.destination}
                        resultIndex={selectedFlight?.itemNo}
                        sessionID={requestBody?.sessionID}
                        flight={selectedFlight}
                        index={fareRuleIndex}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* select flight to share start */}

      {/* ================= SELECTED FLIGHTS MODAL ================= */}
      {/* ================= SELECTED FLIGHTS MODAL ================= */}
      <div className="modal fade" id="shareFlightsInt" tabIndex="-1" aria-labelledby="shareFlightsIntLabel" aria-hidden="true">
        <div className="modal-dialog modal-90 modal-dialog-scrollable">
          <div className="modal-content">

            {/* Modal Header */}
            <div className="modal-header">
              <h4 className="modal-title" id="shareFlightsIntLabel">
                Selected Flight to Share
              </h4>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              <div className="text_flght">
                <h6>Dear Sir/Ma'am,</h6>
                <p>
                  Please find the selected international round-trip flight
                  options below.
                </p>
              </div>

              {selectedShareFlightsInt.length === 0 ? (
                <div className="alert alert-info mt-3">
                  No flights selected. Please select a flight from the
                  flight list.
                </div>
              ) : (
                selectedShareFlightsInt.map((flightIndex, optionIndex) => {
                  const flight = returnFlights[flightIndex];

                  if (!flight) return null;

                  const segments = flight.segments || [];
                  const fares = flight.fares || [];

                  const onwardSegments = segments.filter(
                    (segment) => Number(segment.flightCount) === 1
                  );

                  const returnSegments = segments.filter(
                    (segment) => Number(segment.flightCount) === 2
                  );

                  const allSegments = [
                    ...onwardSegments.map((segment, journeySegmentIndex) => ({
                      ...segment,
                      journeyLabel: "Onward",
                      journeySegmentIndex,
                      journeySegmentCount: onwardSegments.length,
                    })),

                    ...returnSegments.map((segment, journeySegmentIndex) => ({
                      ...segment,
                      journeyLabel: "Return",
                      journeySegmentIndex,
                      journeySegmentCount: returnSegments.length,
                    })),
                  ];

                  const firstOnward = onwardSegments[0];
                  const lastOnward =
                    onwardSegments[onwardSegments.length - 1];

                  const firstReturn = returnSegments[0];
                  const lastReturn =
                    returnSegments[returnSegments.length - 1];

                  const formatAirport = (code) => code || "—";

                  const formatSegmentDateTime = (value) => {
                    if (!value) return "—";

                    const date = new Date(value);

                    if (Number.isNaN(date.getTime())) {
                      return value;
                    }

                    return date.toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                  };

                  const formatFare = (fare) => {
                    const amount = fare.totalFare ?? fare.roundTripFare ?? 0;

                    return Number(amount).toLocaleString("en-IN");
                  };

                  return (
                    <div
                      key={flight.itemNo ?? flightIndex}
                      className="mt-4"
                    >
                      {/* Option Heading */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="optionIndex_list">
                          Option {optionIndex + 1}
                        </h6>

                        <button
                          type="button"
                          className="btn btn-remove"
                          onClick={() => {
                            setSelectedShareFlightsInt((prev) =>
                              prev.filter((index) => index !== flightIndex)
                            );
                          }}
                          aria-label={`Remove option ${optionIndex + 1}`}
                        >
                          <img src="/assets/images/delete.png" width={30} />
                        </button>
                      </div>

                      {/* Route and Travel Dates */}
                      {/* <div className="table-responsive mb-3">
                        <table className="table table-bordered text-center align-middle mb-0">
                          <thead
                            style={{
                              background: "#110942",
                              color: "#fff",
                            }}
                          >
                            <tr>
                              <th style={{ color: "#fff" }}>Journey</th>
                              <th style={{ color: "#fff" }}>Route</th>
                              <th style={{ color: "#fff" }}>Travel Date</th>
                            </tr>
                          </thead>

                          <tbody>
                            {firstOnward && (
                              <tr>
                                <td>Onward</td>

                                <td>
                                  {formatAirport(
                                    firstOnward.departAirportCode
                                  )}

                                  <i
                                    className="fa fa-arrow-right mx-2"
                                    aria-hidden="true"
                                  />

                                  {formatAirport(
                                    lastOnward?.arrivalAirportCode
                                  )}
                                </td>

                                <td>
                                  {formatSegmentDateTime(
                                    firstOnward.departDateTime
                                  )}
                                </td>
                              </tr>
                            )}

                            {firstReturn && (
                              <tr>
                                <td>Return</td>

                                <td>
                                  {formatAirport(
                                    firstReturn.departAirportCode
                                  )}

                                  <i
                                    className="fa fa-arrow-right mx-2"
                                    aria-hidden="true"
                                  />

                                  {formatAirport(
                                    lastReturn?.arrivalAirportCode
                                  )}
                                </td>

                                <td>
                                  {formatSegmentDateTime(
                                    firstReturn.departDateTime
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div> */}

                      {/* Flight Details and Multiple Fare Options */}
                      <div className="table-responsive">
                        <table className="table text-center align-middle table-bordered">
                          <thead
                            style={{
                              background: "#110942",
                              color: "#fff",
                            }}
                          >
                            <tr>
                              <th style={{ color: "#fff" }}>Journey</th>
                              <th style={{ color: "#fff" }}>Flight</th>
                              <th style={{ color: "#fff" }}>Departure</th>
                              <th style={{ color: "#fff" }}>Arrived</th>
                              <th style={{ color: "#fff" }}>Duration</th>
                              <th style={{ color: "#fff" }}>Stops</th>
                              <th style={{ color: "#fff" }}>Fare</th>
                            </tr>
                          </thead>

                          <tbody>
                            {allSegments.length === 0 ? (
                              <tr>
                                <td colSpan={8}>
                                  No segment details are available for this flight.
                                </td>
                              </tr>
                            ) : (
                              allSegments.map((segment, segmentIndex) => {
                                const isFirstSegmentOfJourney =
                                  segment.journeySegmentIndex === 0;

                                return (
                                  <tr
                                    key={`${flight.itemNo ?? flightIndex}-${segmentIndex}`}
                                  >
                                    {/* Journey */}
                                    <td>{segment.journeyLabel}</td>

                                    {/* Airline and Flight Number */}
                                    <td>
                                      <div className="d-flex align-items-center justify-content-center gap-2">
                                        {segment.airLineCode && (
                                          <img
                                            src={`/assets/images/Flight_Images/${segment.airLineCode}.gif`}
                                            alt={segment.airLineName || ""}
                                            className="flight_i"
                                            style={{
                                              width: 30,
                                              height: "auto",
                                            }}
                                            onError={(e) => {
                                              e.currentTarget.style.display =
                                                "none";
                                            }}
                                          />
                                        )}

                                        <span>
                                          {segment.airLineName || ""}
                                          {" "}
                                          {segment.airLineCode || ""}
                                          {segment.flightNumber
                                            ? `-${segment.flightNumber}`
                                            : ""}
                                        </span>
                                      </div>
                                    </td>

                                    {/* Departure */}
                                    <td>
                                      <strong>
                                        {formatSegmentDateTime(
                                          segment.departDateTime
                                        )}
                                      </strong>

                                      <div>
                                        {formatAirport(
                                          segment.departAirportCode
                                        )}
                                      </div>
                                    </td>

                                    {/* Arrival */}
                                    <td>
                                      <strong>
                                        {formatSegmentDateTime(
                                          segment.arrivalDateTime
                                        )}
                                      </strong>

                                      <div>
                                        {formatAirport(
                                          segment.arrivalAirportCode
                                        )}
                                      </div>
                                    </td>

                                    {/* Duration */}
                                    <td>
                                      {segment.duration ||
                                        flight.totalDuration ||
                                        "—"}
                                    </td>

                                    {/* Stops - one cell per journey */}
                                    {isFirstSegmentOfJourney && (
                                      <td
                                        rowSpan={Math.max(
                                          segment.journeySegmentCount,
                                          1
                                        )}
                                      >
                                        {segment.journeySegmentCount <= 1
                                          ? "Non-stop"
                                          : `${segment.journeySegmentCount - 1} stop(s)`}
                                      </td>
                                    )}

                                    {/* Route */}

                                    {/* <td>
                                      {(() => {
                                        const routeSegments = (flight.segments || [])
                                          .filter((s) => s.flightCount === segment.flightCount);

                                        const route = routeSegments.length
                                          ? [
                                            routeSegments[0].departAirportCode,
                                            ...routeSegments.map((s) => s.arrivalAirportCode),
                                          ]
                                          : [
                                            segment.departAirportCode,
                                            segment.arrivalAirportCode,
                                          ];

                                        return route
                                          .filter(Boolean)
                                          .map((airport, index) => (
                                            <React.Fragment key={`${airport}-${index}`}>
                                              {index > 0 && " → "}
                                              {formatAirport(airport)}
                                            </React.Fragment>
                                          ));
                                      })()}
                                    </td> */}


                                    {/* Multiple Fare Options - shared across the round trip */}

                                    {segmentIndex === 0 && (
                                      <td
                                        rowSpan={allSegments.length}
                                        style={{ minWidth: "420px" }}
                                      >
                                        <table className="table table-bordered mb-0">
                                          <thead>
                                            <tr>
                                              <th>Fare Type</th>
                                              <th>Amount</th>
                                              <th>Price Type</th>
                                              <th></th>
                                            </tr>
                                          </thead>

                                          <tbody>
                                            {fares.filter(
                                              (_, fareIndex) =>
                                                !removedShareFaresInt.includes(
                                                  `${flightIndex}-${fareIndex}`
                                                )
                                            ).length === 0 ? (
                                              <tr>
                                                <td colSpan={4} className="text-center">
                                                  No fares available
                                                </td>
                                              </tr>
                                            ) : (
                                              fares.map((fare, fareIndex) => {
                                                const fareKey = `${flightIndex}-${fareIndex}`;

                                                // Hide fares that have been cancelled
                                                if (removedShareFaresInt.includes(fareKey)) {
                                                  return null;
                                                }

                                                return (
                                                  <tr key={fareKey}>
                                                    <td>
                                                      {fare.fareName ||
                                                        fare.fareType ||
                                                        fare.type ||
                                                        `Fare ${fareIndex + 1}`}
                                                    </td>

                                                    <td>
                                                      <i
                                                        className="fa fa-inr"
                                                        aria-hidden="true"
                                                      />{" "}
                                                      {formatFare(fare)}
                                                    </td>

                                                    <td
                                                      style={{
                                                        color: fare.isRefundable ? "green" : "red",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      {fare.isRefundable
                                                        ? "Refundable"
                                                        : "Non-refundable"}
                                                    </td>

                                                    <td className="text-center">
                                                      <img
                                                        src="/assets/images/cancelled.png"
                                                        alt="Remove fare"
                                                        title="Remove fare"
                                                        className="imgCancel"
                                                        onClick={() => {
                                                          setRemovedShareFaresInt((prev) =>
                                                            prev.includes(fareKey)
                                                              ? prev
                                                              : [...prev, fareKey]
                                                          );
                                                        }}
                                                      />
                                                    </td>
                                                  </tr>
                                                );
                                              })
                                            )}
                                          </tbody>
                                        </table>
                                      </td>
                                    )}

                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                disabled={selectedShareFlightsInt.length === 0}
                onClick={() => {
                  const selectedFlights = getSelectedShareDataInt();

                  if (!selectedFlights.length) {
                    window.alert("Please select at least one flight option.");
                    return;
                  }

                  const message = selectedFlights
                    .map((option) => {
                      const onward = option.onwardRoute.join(" → ");
                      const returning = option.returnRoute.join(" → ");

                      return (
                        `Option ${option.optionIndex}\n` +
                        `Onward: ${onward || "—"}\n` +
                        `Return: ${returning || "—"}`
                      );
                    })
                    .join("\n\n");

                  const whatsappUrl =
                    "https://wa.me/?text=" + encodeURIComponent(message);
                  window.open(whatsappUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <img
                  src="/assets/images/whatsapp.png"
                  alt="Share flights on WhatsApp"
                  width={20}
                />
                {" "}WhatsApp
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCopySelectedFlightsInt}
                disabled={selectedShareFlightsInt.length === 0}
              >
                <img
                  src="/assets/images/copy-b.png"
                  alt="Copy HTML"
                  width={20}
                />
                {" "}Copy
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadSelectedFlightsInt}
                disabled={selectedShareFlightsInt.length === 0}
              >
                <img
                  src="/assets/images/download-w.png"
                  alt="Download Excel"
                  width={20}
                />
                {" "}Download
              </button>
            </div>



          </div>
        </div>
      </div>
      {/* select flight to share End */}



    </>
  );
};

export default IntRoundTrip;
